import { useQuery } from '@tanstack/react-query';
import { Activity, CheckCircle2, Clock, XCircle } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  PageSpinner,
  SkeletonKpiCard,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { formatNumber } from '@/lib/utils/format';
import { operatorLabel } from '@/lib/utils/phone';
import { fetchUsageAnalytics, type UsageAnalytics } from './api';

export function UsageAnalyticsPage() {
  const query = useQuery({
    queryKey: ['analytics', 'usage'],
    queryFn: () => fetchUsageAnalytics({ granularity: 'DAILY' }),
  });

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-fg">Usage Analytics</h1>
          <p className="mt-0.5 text-sm text-fg-muted">Verification trends and operator distribution.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <SkeletonKpiCard key={i} />)}
        </div>
        <PageSpinner />
      </div>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        message={isApiError(query.error) ? query.error.firstErrorMessage : 'Could not load analytics.'}
      />
    );
  }

  const data = query.data!;
  const successRate = data.totalRequests === 0 ? 0 : data.successfulRequests / data.totalRequests;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-semibold text-fg">Usage Analytics</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          Verification trends, operator distribution, and service-type breakdown.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Activity className="h-4.5 w-4.5" />}
          label="Total requests"
          value={formatNumber(data.totalRequests)}
          accent="primary"
        />
        <StatCard
          icon={<CheckCircle2 className="h-4.5 w-4.5" />}
          label="Successful"
          value={formatNumber(data.successfulRequests)}
          subtitle={`${(successRate * 100).toFixed(1)}% match rate`}
          accent="success"
        />
        <StatCard
          icon={<XCircle className="h-4.5 w-4.5" />}
          label="No match"
          value={formatNumber(data.failedRequests)}
          accent="danger"
        />
        <StatCard
          icon={<Clock className="h-4.5 w-4.5" />}
          label="Avg latency"
          value={`${Math.round(data.averageLatencyMs)}ms`}
          accent="warning"
        />
      </div>

      {/* Volume chart */}
      <Card noPadding elevated>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <CardHeader className="m-0">
            <CardTitle>Daily volume</CardTitle>
          </CardHeader>
          <div className="flex items-center gap-4 text-xs text-fg-muted">
            <ChartLegend color="bg-primary-500" label="Match" />
            <ChartLegend color="bg-rose-400/70" label="No match" />
          </div>
        </div>
        <PremiumSeriesChart data={data} />
      </Card>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By operator</CardTitle>
          </CardHeader>
          <BreakdownBars values={data.byOperator} transformLabel={operatorLabel} color="primary" />
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>By service type</CardTitle>
          </CardHeader>
          <BreakdownBars
            values={data.byServiceType}
            transformLabel={(k) =>
              k === 'VERIFY_N_PLUS' ? 'Verify N+' :
              k === 'VERIFY_N' ? 'Verify N' :
              k === 'OPPORTUNITY' ? 'Opportunity' : k
            }
            color="violet"
          />
        </Card>
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

type AccentTone = 'primary' | 'success' | 'danger' | 'warning';

const accentMap: Record<AccentTone, { icon: string; card: string }> = {
  primary: {
    icon:  'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400',
    card:  'card-accent-primary',
  },
  success: {
    icon:  'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
    card:  'card-accent-success',
  },
  danger: {
    icon:  'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400',
    card:  'card-accent-danger',
  },
  warning: {
    icon:  'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
    card:  'card-accent-warning',
  },
};

function StatCard({
  icon,
  label,
  value,
  subtitle,
  accent = 'primary',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  accent?: AccentTone;
}) {
  const { icon: iconCls, card: cardCls } = accentMap[accent];
  return (
    <div className={`card-accent-top relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card transition-shadow hover:shadow-card-md dark:shadow-card-dark sm:p-5 ${cardCls}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-fg-muted">{label}</div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-fg">{value}</div>
          {subtitle && <div className="mt-1 text-[11px] text-fg-muted">{subtitle}</div>}
        </div>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconCls}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

const CHART_HEIGHT = 200;

function PremiumSeriesChart({ data }: { data: UsageAnalytics }) {
  if (data.series.length === 0) {
    return <EmptyState message="No activity data in the selected window." />;
  }

  const max = data.series.reduce((m, b) => Math.max(m, b.total), 0) || 1;

  return (
    <div className="px-5 pb-6 pt-5">
      {/* Y-axis hint */}
      <div className="mb-2 flex justify-between text-[10px] text-fg-subtle">
        <span>{max.toLocaleString()}</span>
        <span>0</span>
      </div>
      <div
        className="flex w-full items-end gap-1 sm:gap-1.5"
        style={{ height: `${CHART_HEIGHT}px` }}
        role="img"
        aria-label="Daily volume bar chart"
      >
        {data.series.map((bucket) => {
          const totalPct = (bucket.total / max) * 100;
          const successPct = bucket.total === 0 ? 0 : (bucket.success / bucket.total) * totalPct;
          const failedPct = totalPct - successPct;
          return (
            <div key={bucket.bucket} className="group flex flex-1 flex-col items-center gap-2">
              {/* Bar stack */}
              <div
                className="flex w-full flex-col-reverse overflow-hidden rounded-t-sm"
                style={{ height: `${(bucket.total / max) * CHART_HEIGHT}px`, minHeight: bucket.total > 0 ? '2px' : '0' }}
                title={`${bucket.bucket}: ${bucket.total} total, ${bucket.success} match, ${bucket.failed} no match`}
              >
                {/* Success segment */}
                <div
                  className="w-full transition-all"
                  style={{
                    height: `${successPct === 0 ? 0 : (bucket.success / bucket.total) * 100}%`,
                    background: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)',
                  }}
                />
                {/* Failed segment */}
                {failedPct > 0 && (
                  <div
                    className="w-full"
                    style={{
                      height: `${(bucket.failed / bucket.total) * 100}%`,
                      background: 'rgba(251,113,133,0.75)',
                    }}
                  />
                )}
              </div>
              {/* X-axis label */}
              <span
                className="truncate text-[9.5px] text-fg-subtle transition-colors group-hover:text-fg-muted"
                title={bucket.bucket}
              >
                {bucket.bucket.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChartLegend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-sm ${color}`} aria-hidden />
      {label}
    </span>
  );
}

function BreakdownBars({
  values,
  transformLabel,
  color = 'primary',
}: {
  values: Record<string, number>;
  transformLabel?: (key: string) => string;
  color?: 'primary' | 'violet';
}) {
  const entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, v]) => sum + v, 0);

  if (entries.length === 0) {
    return <p className="text-xs text-fg-subtle">No data for this period.</p>;
  }

  const barGradient = color === 'violet'
    ? 'linear-gradient(90deg,#a78bfa,#7c3aed)'
    : 'linear-gradient(90deg,#60a5fa,#2563eb)';

  return (
    <div className="space-y-3.5">
      {entries.map(([key, value]) => {
        const pct = total === 0 ? 0 : (value / total) * 100;
        const label = transformLabel ? transformLabel(key) : key;
        return (
          <div key={key}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-fg">{label}</span>
              <span className="text-fg-muted">
                {formatNumber(value)}
                <span className="ml-1.5 text-[10px] text-fg-subtle">
                  {pct.toFixed(1)}%
                </span>
              </span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-subtle">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: barGradient }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
